import { useState } from 'react'
import { useDashReport } from '../hooks/useDashReport'
import { HorizontalBarChart } from '../components'

interface ItemConPct {
  nombre: string
  total: number
  porcentaje: number
}

interface HumanovaReport {
  titulo: string
  subtitulo: string
  total: number
  por_provincia: ItemConPct[]
}

export function HumanovaPeruPage() {
  const { data, isLoading, error } = useDashReport<HumanovaReport>('humanova/peru')
  const [provinciasOpen, setProvinciasOpen] = useState(false)

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500" />
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto flex items-center justify-center min-h-[400px]">
          <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
        </div>
      </div>
    )
  }

  const provincias = data.por_provincia
  const provinciaPrincipal = provincias[0]

  const chartProvincias = provincias
    .slice(0, 15)
    .map(p => ({ label: p.nombre, value: p.total, color: '#2fbc80' }))

  return (
    <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center">
              <img src="/logo-tonal-negro.png" alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
              <div>
                <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1 lg:mb-2">{data.titulo}</h1>
                <p className="text-sm lg:text-base text-gray-600 flex items-center gap-2">
                  <span>🇵🇪</span> {data.subtitulo}
                </p>
              </div>
            </div>
            <a
              href="https://enterprise.tonalstrategies.com/pricing/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm"
            >
              Calculador
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          </div>
        </div>

        {/* Estadisticas Generales */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 lg:p-6 border border-green-200 mb-6">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
            <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Resumen General
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-gray-900">{data.total.toLocaleString('es-PE')}</p>
              <p className="text-sm text-gray-600 mt-1">Total Registros</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-green-600">{provincias.length}</p>
              <p className="text-sm text-gray-600 mt-1">Provincias</p>
            </div>
            <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm">
              <p className="text-3xl font-bold text-blue-600">{provinciaPrincipal?.nombre ?? '—'}</p>
              <p className="text-sm text-gray-600 mt-1">
                Provincia Principal{provinciaPrincipal ? ` (${provinciaPrincipal.porcentaje}%)` : ''}
              </p>
            </div>
          </div>
        </div>

        {/* Grafica */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center text-base lg:text-lg">
            <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Distribución por Provincia
            <span className="text-xs text-gray-500 ml-2">(Top 15)</span>
          </h3>
          <HorizontalBarChart data={chartProvincias} />
        </div>

        {/* Desglose por Provincia */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
          <button
            onClick={() => setProvinciasOpen(o => !o)}
            className="w-full text-left focus:outline-none group py-3 lg:py-2 hover:bg-gray-50 rounded-lg px-2 cursor-pointer touch-manipulation"
          >
            <div className="font-semibold text-gray-900 flex items-center justify-between group-hover:text-green-600 transition-colors duration-200">
              <div className="flex items-center flex-wrap">
                <svg className="w-5 h-5 text-green-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="text-base lg:text-lg">Desglose por Provincia</span>
                <span className="text-xs lg:text-sm text-gray-500 ml-2 whitespace-nowrap">({provincias.length} provincias)</span>
              </div>
              <svg className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 flex-shrink-0 ml-2 ${provinciasOpen ? 'rotate-180' : ''}`}
                   fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
          {provinciasOpen && (
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full bg-white text-xs lg:text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-left text-xs lg:text-sm font-semibold text-gray-700">Provincia</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Registros</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">%</th>
                    <th className="px-3 py-2 lg:px-4 lg:py-3 text-center text-xs lg:text-sm font-semibold text-gray-700">Distribución</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {provincias.map(p => (
                    <tr key={p.nombre} className="hover:bg-gray-50 transition-colors duration-150">
                      <td className="px-3 py-2 lg:px-4 lg:py-3 font-medium text-gray-900">{p.nombre}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center text-gray-700">{p.total.toLocaleString('es-PE')}</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-semibold text-green-600">{p.porcentaje}%</td>
                      <td className="px-3 py-2 lg:px-4 lg:py-3">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div className="h-2.5 rounded-full" style={{ background: '#2fbc80', width: `${p.porcentaje}%` }} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                  <tr>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 font-bold text-gray-900">TOTAL</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-gray-900">{data.total.toLocaleString('es-PE')}</td>
                    <td className="px-3 py-2 lg:px-4 lg:py-3 text-center font-bold text-green-600">100%</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 text-center text-gray-600 text-xs lg:text-sm">
          <p>&copy; 2026 Tonal Strategies — Base de Datos Humanova Farmacias</p>
        </div>

      </div>
    </div>
  )
}
